/**
 * <dt-inline-search>: reference-style header search.
 * The icon opens a search bar inside the header row; typing (2+ characters) fetches product
 * suggestions from Shopify's predictive search JSON and lists them under the bar.
 * Escape, the close button or a click outside closes it and returns focus to the icon.
 * Sets [data-dt-search-open] on #header-component so CSS can make room on small screens.
 */
class DtInlineSearch extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-dt-search-toggle]');
    this.panel = this.querySelector('[data-dt-search-panel]');
    this.input = this.querySelector('[data-dt-search-input]');
    this.results = this.querySelector('[data-dt-search-results]');
    this.header = this.closest('#header-component');
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.toggle?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.querySelector('[data-dt-search-close]')?.addEventListener('click', () => this.close(true), { signal });
    this.input?.addEventListener('input', () => this.queueSearch(), { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close(true);
      }
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    clearTimeout(this.timer);
    this.header?.removeAttribute('data-dt-search-open');
  }

  get isOpen() {
    return this.panel instanceof HTMLElement && !this.panel.hidden;
  }

  open() {
    if (!(this.panel instanceof HTMLElement)) return;
    this.panel.hidden = false;
    this.toggle?.setAttribute('aria-expanded', 'true');
    this.header?.setAttribute('data-dt-search-open', '');
    if (this.input instanceof HTMLInputElement) {
      this.input.focus();
      if (this.input.value.trim().length >= 2) this.queueSearch();
    }
  }

  /** @param {boolean} [restoreFocus] */
  close(restoreFocus = false) {
    if (!(this.panel instanceof HTMLElement)) return;
    this.panel.hidden = true;
    if (this.results instanceof HTMLElement) this.results.hidden = true;
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.header?.removeAttribute('data-dt-search-open');
    this.request?.abort();
    if (restoreFocus && this.toggle instanceof HTMLElement) this.toggle.focus();
  }

  queueSearch() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(), 200);
  }

  async search() {
    if (!(this.input instanceof HTMLInputElement) || !(this.results instanceof HTMLElement)) return;
    const query = this.input.value.trim();
    this.request?.abort();

    if (query.length < 2) {
      this.results.hidden = true;
      this.results.replaceChildren();
      return;
    }

    this.request = new AbortController();
    const url = new URL(`${this.dataset.suggestUrl || '/search/suggest'}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      if (query !== this.input.value.trim()) return;
      this.render(query, data?.resources?.results?.products ?? []);
    } catch (error) {
      if (error?.name !== 'AbortError') this.render(query, []);
    }
  }

  /** @param {string} query @param {Array<any>} products */
  render(query, products) {
    if (!(this.results instanceof HTMLElement)) return;
    const fragment = document.createDocumentFragment();

    if (products.length) {
      const heading = document.createElement('p');
      heading.className = 'dt-isearch__heading';
      heading.textContent = 'Products';
      fragment.append(heading);

      for (const product of products) {
        const row = document.createElement('a');
        row.className = 'dt-isearch__row';
        row.href = product.url;

        const imageUrl = product.featured_image?.url || product.image;
        if (imageUrl) {
          const image = document.createElement('img');
          const sized = new URL(imageUrl, window.location.origin);
          sized.searchParams.set('width', '160');
          image.src = sized.toString();
          image.alt = '';
          image.width = 72;
          image.height = 80;
          image.loading = 'lazy';
          row.append(image);
        } else {
          const thumb = document.createElement('span');
          thumb.className = 'dt-isearch__thumb';
          row.append(thumb);
        }

        const info = document.createElement('span');
        info.className = 'dt-isearch__info';
        const title = document.createElement('span');
        title.className = 'dt-isearch__title';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'dt-isearch__price';
        price.textContent = this.formatPrice(product.price);
        info.append(title, price);
        row.append(info);
        fragment.append(row);
      }
    } else {
      const empty = document.createElement('p');
      empty.className = 'dt-isearch__empty';
      empty.textContent = `No products found for “${query}”.`;
      fragment.append(empty);
    }

    const all = document.createElement('a');
    all.className = 'dt-isearch__all';
    const searchUrl = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');
    all.href = searchUrl.toString();
    all.textContent = `Search for “${query}” →`;
    fragment.append(all);

    this.results.replaceChildren(fragment);
    this.results.hidden = false;
  }

  /** @param {string | number} price Predictive search returns prices in major units, e.g. "24.99". */
  formatPrice(price) {
    const amount = Number(price);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('dt-inline-search')) customElements.define('dt-inline-search', DtInlineSearch);
